import { DotIcon } from "lucide-react"
import { Badge, Status } from "../ui"
import { EngagementSource, SelectOptions } from "../../types"

export const STATUS_OPTIONS: SelectOptions = [
  {
    id: "active",
    name: "Active",
    icon: <DotIcon size={8} color="#22c55e" strokeWidth={16} />,
    tooltipText: (
      <>
        Enrolled in rate alert emails. The rate <br />
        dashboard is accessible.
      </>
    )
  },
  {
    id: "disable",
    name: "Disable",
    icon: <DotIcon size={8} color="#eab308" strokeWidth={16} />,
    tooltipText: (
      <>
        Not receiving rate alert emails. The rate <br /> dashboard remains
        accessible.
      </>
    )
  },
  {
    id: "delete",
    name: "Delete",
    icon: <DotIcon size={8} color="#dc2626" strokeWidth={16} />,
    tooltipText: (
      <>
        Unsubscribed from rate alert emails. The rate <br />
        dashboard is inaccessible.
      </>
    )
  }
]

export const PENDING_SUBSCRIPTION_STATUS_OPTIONS: SelectOptions = [
  {
    id: "active",
    name: "Active",
    icon: <DotIcon size={8} color="#28B85E" strokeWidth={16} />,
    tooltipText: (
      <>
        Lead has met their target rate & enrolled
        <br /> into rate subscription emails
      </>
    )
  },
  {
    id: "pending",
    name: "Pending",
    icon: <DotIcon size={8} color="#f59e0b" strokeWidth={16} />,
    tooltipText: (
      <>
        The lead has not reached
        <br /> their dream rate.
      </>
    )
  },
  {
    id: "cancelled",
    name: "Cancelled",
    icon: <DotIcon size={8} color="#dc2626" strokeWidth={16} />,
    tooltipText: (
      <>
        Dream rate has been canceled
        <br />
        by the BankingBridge user.
      </>
    )
  },
  {
    id: "delete",
    name: "Deleted",
    icon: <DotIcon size={8} color="#dc2626" strokeWidth={16} />,
    tooltipText: (
      <>
        Dream rate subscription has been deleted. <br />
        No further monitoring or notifications.
      </>
    )
  }
]

export const StatusComponentMap: Record<string, JSX.Element> = {
  active: <Status.Active label="Active" />,
  disable: <Status.Idle label="Disabled" />,
  delete: <Status.Inactive label="Deleted" />
}

export const DreamRateStatusComponentMap: Record<string, JSX.Element> = {
  active: <Status.Active label="Active" />,
  pending: <Status.Pending label="Pending" />,
  cancelled: <Status.Cancelled label="Cancelled" />,
  delete: <Status.Deleted label="Deleted" />
}

export const engagementSources: EngagementSource[] = [
  {
    id: "embed",
    value: Badge.Success,
    tooltip: <span>Submitted through a website embed.</span>
  },
  {
    id: "log",
    value: Badge.Danger,
    tooltip: <span>Manually created from subscription log.</span>
  },
  {
    id: "unknown",
    value: Badge.Disabled,
    tooltip: <span>Source could not be identified.</span>
  },
  {
    id: "bb_sms",
    label: "SMS link",
    value: Badge.Alt,
    tooltip: <span>Created from a dashboard link in SMS.</span>
  },
  {
    id: "api",
    label: "API",
    value: Badge.Info,
    tooltip: <span>Created via API integration.</span>
  },
  {
    id: "zapier",
    value: Badge.Info,
    tooltip: <span>Created via a Zapier workflow.</span>
  },
  {
    id: "email",
    value: Badge.Info,
    tooltip: <span>Created through intro email sign up.</span>
  },
  {
    id: "import",
    value: Badge.Info,
    tooltip: <span>Added by file import.</span>
  },
  {
    id: "connector",
    value: Badge.Info,
    tooltip: <span>Created via CRM connector integration.</span>
  },
  {
    id: "pending_subscription",
    label: "Dream rate converted",
    value: Badge.Info,
    tooltip: <span>Dream rate converted into a subscription.</span>
  }
]

export const EXCLUDED_MISC_DETAILS = [
  "loan_balance",
  "loan_type",
  "loan_term",
  "loan_amount",
  "loan_purpose",
  "property_type",
  "property_value",
  "list_price",
  "residency_type",
  "credit_score",
  "down_payment",
  "cash_out",
  "household_income",
  "location",
  "message",
  "first_name",
  "last_name",
  "street_address",
  "city",
  "state",
  "zipcode",
  "county",
  "street"
]

export const MISC_FIELD_LABELS: Record<string, string> = {
  military_branch: "Military branch",
  employment_status: "Employment status",
  purchase_readiness: "Purchase readiness",
  foreclosure_bankruptcy: "Foreclosure/bankruptcy",
  va_disability_benefits: "VA disability benefits",
  late_payment: "Late Payment",
  current_mortgage_rate: "Current interest rate",
  source: "Source"
}

export const MISC_FIELD_VALUES: Record<string, Record<string, string>> = {
  military_branch: {
    army: "Army",
    marine: "Marines",
    navy: "Navy",
    air_force: "Air Force",
    coast_guard: "Coast Guard",
    national_guard: "National Guard",
    spouse: "Spouse",
    other: "Other",
    civilian: "Civilian"
  },
  purchase_readiness: {
    considering_loan_offers_now: "Considering loan offers now",
    thirty_days_out: "30 days out",
    two_to_four_months: "2-4 months out",
    researching_options: "Researching options"
  },
  employment_status: {
    employed: "Employed",
    self_employed: "Self employed",
    retired: "Retired",
    unemployed: "Unemployed"
  },
  foreclosure_bankruptcy: {
    neither: "Neither",
    bankruptcy: "Bankruptcy",
    foreclosure: "Foreclosure",
    both: "Both"
  },
  va_disability_benefits: {
    yes: "Yes",
    no: "No"
  },
  late_payment: {
    no: "No",
    "1_late": "1 Late Payment",
    "2_late": "2+ Late Payments"
  },
  source: {
    rates_now: "Rates.Now"
  }
}
